import { useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

export interface OptionSelectProps<T extends string> {
  value: T | null | undefined;
  options: readonly { value: T; label: string }[];
  placeholder?: string;
  onChange: (v: T | null) => void;
}

export function OptionSelect<T extends string>({ value, options, placeholder = 'Pick option', onChange }: OptionSelectProps<T>) {
  const displayText = useMemo(() => {
    const found = options.find((o) => o.value === value);
    return found?.label || placeholder;
  }, [options, value, placeholder]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-8">
          <span className="truncate text-left text-xs">{displayText}</span>
          <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px]">
        <Command>
          <CommandInput placeholder="Search" className="h-8" />
          <CommandList>
            <CommandEmpty>No options</CommandEmpty>
            <CommandGroup heading="">
              {options.map((opt) => (
                <CommandItem key={`opt-${opt.value}`} value={opt.label} onSelect={() => onChange(opt.value)}>
                  <Check className={cn('mr-2 h-3 w-3', opt.value === value ? 'opacity-100' : 'opacity-0')} />
                  <span className="text-xs">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default OptionSelect;


